import React, { useEffect, useState } from 'react';
import Ionicons from '@expo/vector-icons/Ionicons';
import { FlatList, KeyboardAvoidingView, Platform, StyleSheet, TouchableOpacity, View, Text, TextInput } from './rn';
import { api, type Conversation } from '../services/api';
import { useTheme } from '../theme/ThemeContext';
import { radius } from '../theme';
import { useTranslation } from '../i18n/I18nContext';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Avatar from './Avatar';
import AppSheet from './AppSheet';

export type ForwardPeer = Pick<Conversation, 'other_id' | 'other_name' | 'other_photo'>;
const searchKey = (text: string) => text.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/ı/g, 'i').trim();

export default function OwnTextForwardSheet({ token, myId, otherId, sourceText, media, onClose, onChoose }: {
  token: string; myId: number; otherId: number; sourceText?: string;media?:boolean;
  onClose: () => void; onChoose: (peer: ForwardPeer) => void;
}) {
  const { t } = useTranslation(); const colors = useTheme(); const insets = useSafeAreaInsets();
  const [peers, setPeers] = useState<ForwardPeer[]>([]);
  const [state, setState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [attempt, setAttempt] = useState(0);
  const [query, setQuery] = useState('');
  useEffect(() => {
    let active = true; setState('loading'); setPeers([]); setQuery('');
    // Metadata only; do not open/read another conversation or decrypt its history.
    api.getConversations(token).then(rows => {
      if (!active) return;
      setPeers(rows.filter(p => p.other_id !== myId && p.other_id !== otherId)
        .map(({other_id, other_name, other_photo}) => ({other_id, other_name, other_photo})));
      setState('ready');
    }).catch(() => { if (active) setState('error'); });
    return () => { active = false; };
  }, [token, myId, otherId, attempt]);
  const label = { color: colors.black, fontSize: 16 };
  const filtered = peers.filter(peer => searchKey(peer.other_name ?? '').includes(searchKey(query)));
  return <AppSheet visible title={t('message.forwardOwn')} onClose={onClose} testID="forward" scrollable={false}>
    <KeyboardAvoidingView style={styles.body} behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
      <FlatList data={state === 'ready' ? filtered : []} keyExtractor={p => String(p.other_id)}
        keyboardShouldPersistTaps="handled" keyboardDismissMode="on-drag"
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 20 }]}
        ListHeaderComponent={<View>
          {!!sourceText && <View style={[styles.preview, {backgroundColor: colors.gray100, borderColor: colors.gray400}]}>
            <Ionicons name="arrow-redo-outline" size={20} color={colors.gray600} accessible={false} />
            <Text testID="forward-preview" style={[label, styles.previewText]} numberOfLines={3}>{sourceText}</Text>
          </View>}
          <Text style={[styles.hint, {color: colors.gray600}]}>{t(media?'message.forwardMediaHint':'message.forwardHint')}</Text>
        </View>}
        ListEmptyComponent={state === 'loading' ? <Text style={label}>{t('common.loading')}</Text>
          : state === 'error' ? <TouchableOpacity testID="forward-retry" accessibilityRole="button" style={styles.row} onPress={() => setAttempt(a => a + 1)}>
            <Text style={label}>{t('common.retry')}</Text></TouchableOpacity>
          : <Text testID="forward-empty" style={[label, styles.hint]}>{t(query.trim() ? 'inbox.searchEmpty' : 'message.forwardEmpty')}</Text>}
        renderItem={({item}) => <TouchableOpacity testID={`forward-peer-${item.other_id}`} accessibilityRole="button"
          accessibilityLabel={item.other_name} accessibilityHint={t(media?'message.forwardMediaHint':'message.forwardHint')}
          style={[styles.row, {borderBottomColor: colors.gray200}]} onPress={() => onChoose(item)}>
          <Avatar photoUrl={item.other_photo} name={item.other_name} size={44} />
          <Text style={[label, styles.name]}>{item.other_name}</Text>
          <Ionicons name="chevron-forward" size={18} color={colors.gray600} accessible={false} />
        </TouchableOpacity>} />
      <View style={[styles.search, {backgroundColor: colors.gray100, marginBottom: Math.max(insets.bottom, 12)}]}>
        <Ionicons name="search" size={20} color={colors.gray600} accessible={false} />
        <TextInput testID="forward-search" value={query} onChangeText={setQuery} placeholder={t('common.search')}
          accessibilityLabel={t('common.search')} placeholderTextColor={colors.gray600} style={[label, styles.input]}
          autoCorrect={false} autoCapitalize="none" returnKeyType="search" />
        {!!query && <TouchableOpacity testID="forward-search-clear" accessibilityRole="button" accessibilityLabel={t('common.clear')}
          style={styles.clear} onPress={() => setQuery('')}><Ionicons name="close-circle" size={22} color={colors.gray600} /></TouchableOpacity>}
      </View>
    </KeyboardAvoidingView>
  </AppSheet>;
}
const styles = StyleSheet.create({
  body: {flex: 1, minHeight: 0},
  content: {paddingHorizontal: 20},
  row: {minHeight: 64, flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 10, borderBottomWidth: StyleSheet.hairlineWidth},
  name: {flex: 1, minWidth: 0, flexShrink: 1},
  preview: {flexDirection: 'row', alignItems: 'flex-start', gap: 10, borderStartWidth: 3, borderRadius: radius.control, padding: 12, marginTop: 8},
  previewText: {flex: 1, minWidth: 0},
  hint: {fontSize: 14, marginVertical: 16},
  search: {flexDirection: 'row', alignItems: 'center', borderRadius: 24, marginHorizontal: 16, paddingStart: 14, marginTop: 8},
  input: {flex: 1, minWidth: 0, minHeight: 48, paddingHorizontal: 10},
  clear: {width: 48, minHeight: 48, alignItems: 'center', justifyContent: 'center'},
});
